// Centralized, typed access to environment flags and variables.
//
// This is a Vite app, not Create React App: `process.env` does not exist in
// the browser bundle (Vite only special-cases `process.env.NODE_ENV` for
// library compatibility - nothing else). Client-exposed env vars must be
// read from `import.meta.env` and MUST be prefixed `VITE_` in .env for
// Vite to expose them at all. Previously `API_BASE_URL` was hardcoded
// directly here with no env var read at all, so switching environments
// (local / staging / production) required editing this file and
// redeploying - see .env.example at the project root: copy it to .env
// and fill in the right value by hand for whichever environment you're
// running against.

export type NodeEnvironment = 'development' | 'production' | 'test';

export const NODE_ENV: NodeEnvironment = (import.meta.env.MODE ||
  'development') as NodeEnvironment;

export const isDev = import.meta.env.DEV;
export const isProd = import.meta.env.PROD;
export const isTest = NODE_ENV === 'test';

// Match the existing behavior: enable logging only in development
export const logging = isDev;

// Global API configuration, read from VITE_API_BASE_URL (see the
// .env.* files). Falls back to localhost so a fresh checkout with no env
// file configured still runs against a local backend instead of silently
// hitting production.
export const API_BASE_URL: string =
  import.meta.env.VITE_API_BASE_URL || 'https://api.clearscan.ca';

if (isDev) {
  console.log(`🌐 API_BASE_URL (mode: ${NODE_ENV}):`, API_BASE_URL);
}

// Typed helpers for reading environment variables safely. Vite only ever
// exposes vars prefixed VITE_ to client code - callers should pass the
// full "VITE_..." name.
export function getEnv(name: string, defaultValue?: string): string {
  const value = import.meta.env[name] as string | undefined;
  if (value === undefined) {
    if (defaultValue !== undefined) return defaultValue;
    throw new Error(`Missing required environment variable: ${name}`);
  }
  return value;
}

export function getBool(name: string, defaultValue = false): boolean {
  const value = import.meta.env[name] as string | undefined;
  if (value === undefined) return defaultValue;
  return ['1', 'true', 'yes', 'on'].includes(value.toLowerCase());
}

export function getInt(name: string, defaultValue?: number): number {
  const value = import.meta.env[name] as string | undefined;
  if (value === undefined) {
    if (defaultValue !== undefined) return defaultValue;
    throw new Error(`Missing required environment variable: ${name}`);
  }
  const parsed = parseInt(value, 10);
  if (Number.isNaN(parsed)) {
    throw new Error(
      `Invalid integer for environment variable ${name}: ${value}`
    );
  }
  return parsed;
}
